import React from 'react';
import { isArray, isEqual, some } from 'lodash';
import { useDispatch } from 'react-redux';
import { useIntl, type IntlShape } from 'react-intl';
import { type Dispatch } from 'redux';
import { type TFunction } from 'i18next';

import { getDefaultModifiedModelAdd } from 'app/main/configs';
import useSettings from '../hooks/useSettings';
import useUserData from '../hooks/useUserData';
import { alertApiError, alertProgressMsg, alertSuccessMsg, executeStored } from '../Utils';
import { deleteFile, uploadFiles } from '../files-explorer/FilesUtils';

export interface ModelEventsParams {
	dispatch: Dispatch<any>;
	t: TFunction;
	sysSettings: any;
	model: any;
	modifiedModel: any;
	isAdd?: boolean;
	intl: IntlShape;
}

export type onBeforeSaveType = (params: ModelEventsParams) => Promise<any>;
export type onAfterSaveType = (params: ModelEventsParams) => Promise<any>;

export interface ModelType {
	model: any;
	modifiedModel: any;
	setModel: React.Dispatch<React.SetStateAction<any>>;
	setModifiedModel: React.Dispatch<React.SetStateAction<any>>;
	hasModelChanged: () => boolean;
	resetModifiedModel: (newModel?: any) => void;
	sendChange: (name: string, value: any) => void;
	sendFilesChange: (name: string, path: any, file: File | null) => void;
	save: ({
		storedName,
		onBeforeSave,
		onAfterSave,
		onSuccess,
		onError
	}: {
		storedName?: string;
		onBeforeSave?: onBeforeSaveType;
		onAfterSave?: onAfterSaveType;
		onSuccess?: (params: ModelEventsParams) => void;
		onError?: (error) => void;
	}) => void;
}

const cleanModel = (model: any) => {
	Object.entries(model).forEach(e => {
		const [key, value] = e;
		if (typeof value === 'string') model[key] = model[key].replace(/'/g, '’');
		if (typeof value === 'boolean') model[key] = model[key] ? 1 : 0;
	});
	return model;
};

export default function useModel(props: {
	model?: any;
	baseModel?: any;
	baseModifiedModel?: any;
	settings: any;
	isEdit: boolean;
	isAdd?: boolean;
}) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();
	const userData = useUserData();
	const intl = useIntl();

	const eventsParams = { dispatch, t, sysSettings, isAdd: props.isAdd, intl };

	const isEdit = props.isEdit === undefined ? true : props.isEdit;

	const [defaultModifiedModelAdd] = React.useState(
		getDefaultModifiedModelAdd({ settings: props.settings, sysSettings, t, userData })
	);

	const [model, setModel] = React.useState(props.model || {});
	const [modifiedModel, setModifiedModel] = React.useState({
		...(props.isAdd ? defaultModifiedModelAdd : {}),
		...(props.baseModifiedModel || {}),
		...(props.baseModel || {})
	});

	const [files, setFiles] = React.useState<{ name: string; path: string; file: File | null }[]>([]);
	const [saved, setSaved] = React.useState(false);

	const resetModifiedModel = (newModel?: any) => {
		setModifiedModel({
			...(props.isAdd ? defaultModifiedModelAdd : {}),
			...(props.baseModel || {}),
			...(newModel || {})
		});
	};

	const detectChanges = (value, oldValue) => {
		if (isArray(value) && some(value, (item, index) => !isEqual(item, oldValue?.at(index)))) return true;
		if (!isEqual(value !== undefined ? value : null, oldValue !== undefined ? oldValue : null)) return true;

		return false;
	};

	const sendChange: ModelType['sendChange'] = React.useCallback(
		(name, value) => {
			setModifiedModel(prevState => {
				const oldValue = prevState[name] !== undefined ? prevState[name] : model[name];

				if (typeof value === 'string') value = value.replace(/'/g, '’');
				if (typeof value === 'boolean') value = value ? 1 : 0;

				if (detectChanges(value, oldValue)) {
					return { ...prevState, [name]: value };
				}

				return prevState;
			});
		},
		[model]
	);

	const sendFilesChange: ModelType['sendFilesChange'] = (name, path, file) => {
		const temp = files.filter(f => f?.name !== name && file !== null);

		if (file !== null) {
			sendChange(name, file.name);
			setFiles([...temp, { name, path, file }]);
		} else {
			sendChange(name, null);
			setFiles([...temp, { name, path, file }]);
		}
	};

	const handleFiles = () => {
		files.forEach(({ name, path, file }) => {
			if (file !== null) uploadFiles({ path, files: [file], dispatch });
			else if (!props.isAdd) deleteFile({ path: `${path}\\${model[name]}`, dispatch });
		});
	};

	const save: ModelType['save'] = params => {
		dispatch(alertProgressMsg(t));

		const data = {
			...modifiedModel,
			[`${props.settings.modu_short}_user_${props.isAdd ? 'created' : 'modified'}_uid`]: userData.user_uid
		};

		const onBeforeSavePromise = params.onBeforeSave?.({ ...eventsParams, model, modifiedModel: data });

		(onBeforeSavePromise || Promise.resolve())
			.then(beforeSaveData => {
				const action = params.storedName || `st_${props.settings.modu_title}_${props.isAdd ? 'add' : 'update'}`;
				return executeStored({ action, dispatch, data: cleanModel(beforeSaveData || data) });
			})
			.then(response => {
				setModel(response.data);
				resetModifiedModel();
				handleFiles();
				setSaved(true);

				if (params.onAfterSave) {
					return params.onAfterSave({ ...eventsParams, model: response.data, modifiedModel: data });
				} else return Promise.resolve(response.data);
			})
			.then(result => {
				if (params.onSuccess) params.onSuccess({ ...eventsParams, model: result, modifiedModel: data });
				else dispatch(alertSuccessMsg(t, props.isAdd ? 'ALERT_ADD_SUCCESS' : 'ALERT_UPDATE_SUCCESS'));
			})
			.catch(error => {
				if (params.onError) params.onError(error);
			});
	};

	const hasModelChanged = () => {
		if (!isEdit || saved) return false;
		const changes = Object.keys(modifiedModel)
			.filter(
				key =>
					key !== `${props.settings.modu_short}_uid` &&
					key !== `${props.settings.modu_short}_user_created_uid` &&
					key !== `${props.settings.modu_short}_user_modified_uid`
			)
			.filter(key => detectChanges(props.baseModel[key], modifiedModel[key]));

		return changes.length > 0;
	};

	return {
		model: { ...model, ...(isEdit ? modifiedModel : {}) },
		modifiedModel,
		setModel,
		setModifiedModel,
		hasModelChanged,
		resetModifiedModel,
		sendChange,
		sendFilesChange,
		save
	} as ModelType;
}
